import type { TemplateCard, TemplateTag } from '@/lib/cms'
import type { BlogPost, BlogPostCard, Clonable } from '@/lib/content'

import { siteConfig } from '@/constants/site'

// Central schema.org (JSON-LD) builders. Every page emits a single `@graph`
// document that shares one Organization and WebSite node (referenced by @id),
// then layers page-specific nodes (CollectionPage, ItemList, FAQPage, ...).

const SITE_URL = siteConfig.url.replace(/\/$/, '')

const ORGANIZATION_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

const SOCIAL_LINKS = [
    'https://www.linkedin.com/company/wedoflow/',
    'https://www.instagram.com/wedoflow/',
    'https://www.facebook.com/wedoflow/',
    'https://x.com/wedoflow',
    'https://www.tiktok.com/@wedoflow',
    'https://www.youtube.com/channel/UC2FWFwbfxsWbxUayV1-6Gzg'
]

type SchemaNode = Record<string, unknown>

export function absoluteUrl(path = ''): string {
    if (!path) return SITE_URL
    if (path.startsWith('http')) return path

    return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

function graph(nodes: SchemaNode[]): SchemaNode {
    return {
        '@context': 'https://schema.org',
        '@graph': nodes
    }
}

/* Shared nodes */

function organizationNode(): SchemaNode {
    return {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: siteConfig.name,
        url: SITE_URL,
        description: siteConfig.description,
        logo: {
            '@type': 'ImageObject',
            url: absoluteUrl('https://wedoflow.cdn.modulify.website/images/Wedoflow-logo.svg')
        },
        image: absoluteUrl(siteConfig.ogImage ?? 'https://wedoflow.cdn.modulify.website/images/Wedoflow-logo.svg'),
        sameAs: SOCIAL_LINKS
    }
}

function websiteNode(): SchemaNode {
    return {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: siteConfig.name,
        url: SITE_URL,
        description: siteConfig.description,
        inLanguage: ['en', 'de', 'fr'],
        publisher: { '@id': ORGANIZATION_ID }
    }
}

interface WebPageInput {
    type?: string
    path: string
    name: string
    description?: string
    primaryEntityId?: string
}

function webPageNode({ type = 'WebPage', path, name, description, primaryEntityId }: WebPageInput): SchemaNode {
    const url = absoluteUrl(path)

    return {
        '@type': type,
        '@id': `${url}#webpage`,
        url,
        name,
        ...(description ? { description } : {}),
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': ORGANIZATION_ID },
        inLanguage: 'en',
        ...(primaryEntityId ? { mainEntity: { '@id': primaryEntityId } } : {})
    }
}

interface Crumb {
    name: string
    path: string
}

function breadcrumbNode(crumbs: Crumb[]): SchemaNode {
    return {
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: absoluteUrl(crumb.path)
        }))
    }
}

function listWrap(elements: SchemaNode[]): SchemaNode[] {
    return elements.map((element, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        ...element
    }))
}

function priceOffer(price: string | null, url: string): SchemaNode | null {
    if (!price) return null

    const match = price.replace(/,/g, '').match(/\d+(\.\d+)?/)

    if (!match) return null

    return {
        '@type': 'Offer',
        price: match[0],
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url
    }
}

/* Page builders */

export function homeSchema(name?: string, description?: string): SchemaNode {
    return graph([
        organizationNode(),
        websiteNode(),
        webPageNode({
            path: '/',
            name: name ?? `${siteConfig.name} — Premium Webflow Templates Marketplace`,
            description: description ?? siteConfig.description
        })
    ])
}

interface InsightsSchemaOptions {
    name?: string
    description?: string
    breadcrumbHome?: string
    breadcrumbInsights?: string
}

export function insightsSchema(posts: BlogPostCard[], options: InsightsSchemaOptions = {}): SchemaNode {
    const listId = `${absoluteUrl('/insights')}#postlist`
    const name = options.name ?? 'Wedoflow Insights'
    const description = options.description ?? 'Webflow tips, guides and insights from the Wedoflow team. Learn how to build high-performing websites faster.'
    const breadcrumbHome = options.breadcrumbHome ?? 'Home'
    const breadcrumbInsights = options.breadcrumbInsights ?? 'Insights'

    const elements = posts.map((post) => {
        const image = post.main_image || post.thumbnail_image

        return {
            item: {
                '@type': 'BlogPosting',
                headline: post.name,
                url: absoluteUrl(`/post/${post.slug}`),
                ...(image ? { image: absoluteUrl(image) } : {}),
                ...(post.post_summary ? { description: post.post_summary } : {}),
                ...(post.publish_date ? { datePublished: post.publish_date } : {}),
                // dateModified only when the post was genuinely edited after publication —
                // declaring it otherwise is the 'artificially freshened date' pattern Google penalises.
                ...(post.updated_at ? { dateModified: post.updated_at } : {}),
                author: { '@id': ORGANIZATION_ID },
                publisher: { '@id': ORGANIZATION_ID }
            }
        }
    })

    return graph([
        organizationNode(),
        websiteNode(),
        webPageNode({
            type: 'CollectionPage',
            path: '/insights',
            name,
            description,
            primaryEntityId: listId
        }),
        {
            '@type': 'ItemList',
            '@id': listId,
            name,
            numberOfItems: elements.length,
            itemListElement: listWrap(elements)
        },
        breadcrumbNode([
            { name: breadcrumbHome, path: '/' },
            { name: breadcrumbInsights, path: '/insights' }
        ])
    ])
}

interface TemplatesSchemaInput {
    path: string
    name: string
    description: string
    breadcrumbName: string
    breadcrumbHome?: string
    templates: TemplateCard[]
}

export function templatesSchema({ path, name, description, breadcrumbName, breadcrumbHome, templates }: TemplatesSchemaInput): SchemaNode {
    const listId = `${absoluteUrl(path)}#templatelist`
    const crumbHome = breadcrumbHome ?? 'Home'

    const elements = templates.map((template) => {
        const url = absoluteUrl(`/t/${template.slug}`)
        const offer = priceOffer(template.price, url)

        return {
            item: {
                '@type': 'Product',
                name: template.name,
                url,
                ...(template.thumbnail_image ? { image: absoluteUrl(template.thumbnail_image) } : {}),
                ...(template.template_category ? { category: template.template_category } : {}),
                brand: { '@type': 'Brand', name: siteConfig.name },
                ...(offer ? { offers: offer } : {})
            }
        }
    })

    return graph([
        organizationNode(),
        websiteNode(),
        webPageNode({
            type: 'CollectionPage',
            path,
            name,
            description,
            primaryEntityId: listId
        }),
        {
            '@type': 'ItemList',
            '@id': listId,
            name,
            numberOfItems: elements.length,
            itemListElement: listWrap(elements)
        },
        breadcrumbNode([
            { name: crumbHome, path: '/' },
            { name: breadcrumbName, path }
        ])
    ])
}

export function tagsSchema(tags: TemplateTag[], description: string, schemaName?: string, breadcrumbHome?: string, breadcrumbTags?: string): SchemaNode {
    const listId = `${absoluteUrl('/tags')}#taglist`
    const name = schemaName ?? 'Webflow Template Tags'
    const crumbHome = breadcrumbHome ?? 'Home'
    const crumbTags = breadcrumbTags ?? 'Tags'

    const elements = tags.map((tag) => ({
        name: tag.name,
        url: absoluteUrl(`/tags/${tag.slug}`)
    }))

    return graph([
        organizationNode(),
        websiteNode(),
        webPageNode({
            type: 'CollectionPage',
            path: '/tags',
            name,
            description,
            primaryEntityId: listId
        }),
        {
            '@type': 'ItemList',
            '@id': listId,
            name,
            numberOfItems: elements.length,
            itemListElement: listWrap(elements)
        },
        breadcrumbNode([
            { name: crumbHome, path: '/' },
            { name: crumbTags, path: '/tags' }
        ])
    ])
}

interface CloneableSchemaLabels {
    schemaName?: string
    breadcrumbHome?: string
    breadcrumbCloneable?: string
}

export function cloneableSchema(clonables: Clonable[], description: string, labels: CloneableSchemaLabels = {}): SchemaNode {
    const name = labels.schemaName ?? 'Free Webflow Cloneables'
    const crumbHome = labels.breadcrumbHome ?? 'Home'
    const crumbCloneable = labels.breadcrumbCloneable ?? 'Cloneable'

    const listId = `${absoluteUrl('/cloneable')}#cloneablelist`

    const elements = clonables.map((item) => {
        const url = item.website_link || item.clonable_direct_link

        return {
            item: {
                '@type': 'CreativeWork',
                name: item.name,
                ...(url ? { url } : {}),
                ...(item.item_thumbnail ? { image: absoluteUrl(item.item_thumbnail) } : {}),
                ...(item.small_summary || item.meta_description
                    ? { description: item.small_summary || item.meta_description }
                    : {}),
                isAccessibleForFree: true,
                author: { '@id': ORGANIZATION_ID }
            }
        }
    })

    return graph([
        organizationNode(),
        websiteNode(),
        webPageNode({
            type: 'CollectionPage',
            path: '/cloneable',
            name,
            description,
            primaryEntityId: listId
        }),
        {
            '@type': 'ItemList',
            '@id': listId,
            name,
            numberOfItems: elements.length,
            itemListElement: listWrap(elements)
        },
        breadcrumbNode([
            { name: crumbHome, path: '/' },
            { name: crumbCloneable, path: '/cloneable' }
        ])
    ])
}

interface FaqItem {
    question: string
    answer: string
}

// FAQ questions carry decorative emojis and the answers are HTML; strip emojis
// from the question text and keep the answer HTML (Google allows <a>/<br> in answers).
function cleanQuestion(value: string): string {
    return value
        .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}]/gu, '')
        .replace(/\s+/g, ' ')
        .trim()
}

interface FaqSchemaLabels {
    schemaName?: string
    breadcrumbHome?: string
    breadcrumbFaq?: string
}

export function faqSchema(faqs: FaqItem[], labels: FaqSchemaLabels = {}): SchemaNode {
    const name = labels.schemaName ?? 'Webflow FAQ'
    const crumbHome = labels.breadcrumbHome ?? 'Home'
    const crumbFaq = labels.breadcrumbFaq ?? 'FAQ'

    return graph([
        organizationNode(),
        websiteNode(),
        {
            '@type': 'FAQPage',
            '@id': `${absoluteUrl('/faq')}#webpage`,
            url: absoluteUrl('/faq'),
            name,
            isPartOf: { '@id': WEBSITE_ID },
            inLanguage: 'en',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: cleanQuestion(faq.question),
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer
                }
            }))
        },
        breadcrumbNode([
            { name: crumbHome, path: '/' },
            { name: crumbFaq, path: '/faq' }
        ])
    ])
}

interface ContactSchemaLabels {
    schemaName?: string
    breadcrumbHome?: string
    breadcrumbContact?: string
}

export function contactSchema(description: string, labels: ContactSchemaLabels = {}): SchemaNode {
    const name = labels.schemaName ?? 'Contact Wedoflow'
    const crumbHome = labels.breadcrumbHome ?? 'Home'
    const crumbContact = labels.breadcrumbContact ?? 'Contact'

    return graph([
        {
            ...organizationNode(),
            contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer support',
                url: absoluteUrl('/contact'),
                availableLanguage: ['English', 'German', 'French']
            }
        },
        websiteNode(),
        webPageNode({
            type: 'ContactPage',
            path: '/contact',
            name,
            description
        }),
        breadcrumbNode([
            { name: crumbHome, path: '/' },
            { name: crumbContact, path: '/contact' }
        ])
    ])
}

interface BlogPostSchemaInput {
    post: BlogPost
    authorName?: string | null
    authorSlug?: string | null
    breadcrumbHome?: string
    breadcrumbInsights?: string
}

/**
 * BlogPosting markup for a single article.
 *
 * datePublished is the original publication date and never moves. dateModified is emitted
 * ONLY when the post was genuinely edited afterwards (updated_at is null until a real edit
 * stamps it), because declaring a modification that did not happen is the artificially
 * freshened date pattern search engines discount.
 */
export function blogPostSchema({
    post,
    authorName,
    authorSlug,
    breadcrumbHome = 'Home',
    breadcrumbInsights = 'Insights'
}: BlogPostSchemaInput): SchemaNode {
    const path = `/post/${post.slug}`
    const image = post.main_image || post.thumbnail_image

    return graph([
        organizationNode(),
        websiteNode(),
        {
            '@type': 'BlogPosting',
            '@id': `${absoluteUrl(path)}#article`,
            headline: post.name,
            url: absoluteUrl(path),
            mainEntityOfPage: absoluteUrl(path),
            ...(image ? { image: absoluteUrl(image) } : {}),
            ...(post.meta_description || post.post_summary
                ? { description: post.meta_description || post.post_summary }
                : {}),
            ...(post.publish_date ? { datePublished: post.publish_date } : {}),
            ...(post.updated_at ? { dateModified: post.updated_at } : {}),
            author: authorName
                ? {
                      '@type': 'Person',
                      name: authorName,
                      ...(authorSlug ? { url: absoluteUrl(`/author/${authorSlug}`) } : {})
                  }
                : { '@id': ORGANIZATION_ID },
            publisher: { '@id': ORGANIZATION_ID }
        },
        breadcrumbNode([
            { name: breadcrumbHome, path: '/' },
            { name: breadcrumbInsights, path: '/insights' },
            { name: post.name, path }
        ])
    ])
}
